/* eslint-disable class-methods-use-this */
import { html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { OpenZoomLayerEvent } from '../../@types/dom.js';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import close from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_close.svg?element';
import { togglePageScrolling } from '../../scripts/utils.js';
import '../ai-badge/ai-badge.js';

interface ZoomSlide {
  url: string;
  aiGenerated?: boolean;
}

@customElement('ents-zoom-layer')
export class ZoomLayer extends LitElement {
  @property({ attribute: false }) slides?: ZoomSlide[];
  @property({ type: String, attribute: 'badge-lang' }) badgeLang = 'de';
  @property({ type: String, attribute: 'badge-country' }) badgeCountry = 'de';
  @property({ type: String, attribute: 'active-color-name' }) activeColorName?: string;
  @property({ type: String, attribute: 'brand-name' }) brandName?: string;
  @property({ type: String, attribute: 'product-name' }) productName?: string;
  @property({ type: String, attribute: 'close-title' }) closeTitle?: string;

  private lightbox?: PhotoSwipeLightbox;
  private resizeObservers: ResizeObserver[] = [];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('ents:zoom-layer:open', (event) => this.handleOpenEvent(event));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    togglePageScrolling(true);
    this.disconnectResizeObservers();
    this.lightbox?.destroy();
  }

  firstUpdated() {
    this.initPhotoSwipe();
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('slides')) {
      this.lightbox?.destroy();
      this.initPhotoSwipe();
    }
  }

  private disconnectResizeObservers() {
    this.resizeObservers.forEach((ro) => ro.disconnect());
    this.resizeObservers = [];
  }

  private initPhotoSwipe() {
    this.disconnectResizeObservers();
    if (!this.slides || this.slides.length === 0) return;
    const gallerySelector = this.shadowRoot?.querySelector<HTMLElement>('.pswp-gallery');
    if (!gallerySelector) return;

    this.lightbox = new PhotoSwipeLightbox({
      closeSVG: close().outerHTML,
      gallery: gallerySelector,
      children: 'a',
      imageClickAction: 'zoom',
      zoom: false,
      counter: false,
      padding: { top: 48, bottom: 48, left: 0, right: 0 },
      showHideAnimationType: 'none',
      trapFocus: true,
      returnFocus: true,
      secondaryZoomLevel: (zoomLevel) => {
        return Math.min(4 * zoomLevel.initial, 1);
      },
      maxZoomLevel: (zoomLevel) => {
        return Math.min(4 * zoomLevel.initial, 1);
      },
      closeTitle: this.closeTitle ?? 'Close',
      pswpModule: () => import('photoswipe'),
    });
    this.lightbox.on('uiRegister', () => {
      this.lightbox?.pswp?.element?.setAttribute('aria-modal', 'true');
      this.lightbox?.pswp?.ui?.registerElement({
        name: 'bulletsIndicator',
        className: 'pswp__bullets-indicator',
        appendTo: 'wrapper',
        onInit: (el, pswp) => {
          const bullets: HTMLDivElement[] = [];
          let bullet: HTMLDivElement;
          let prevIndex = -1;

          for (let i = 0; i < pswp.getNumItems(); i++) {
            bullet = document.createElement('div');
            bullet.className = 'pswp__bullet';
            el.appendChild(bullet);
            bullets.push(bullet);
          }

          pswp.on('change', () => {
            if (prevIndex >= 0) {
              bullets[prevIndex].classList.remove('pswp__bullet--active');
            }
            bullets[pswp.currIndex].classList.add('pswp__bullet--active');
            prevIndex = pswp.currIndex;
          });
        },
      });
    });
    this.lightbox.on('close', () => {
      togglePageScrolling(true);
    });
    this.lightbox.on('destroy', () => {
      togglePageScrolling(true);
    });
    this.lightbox.on('contentAppend', ({ content }) => {
      if (content.element) {
        content.element.setAttribute('aria-hidden', 'true');
        content.element.setAttribute('alt', this.getImageAltText());
        content.slide?.container.appendChild(content.element);
      }
      this.attachAiBadgeToSlide(content);
    });
    this.lightbox.on('change', () => {
      const element = this.lightbox?.pswp?.element;
      const currentSlide = this.lightbox?.pswp?.currSlide;
      if (element && currentSlide) {
        element.setAttribute('data-test-ents-zoom-image-index', `${currentSlide.index}`);
        element.setAttribute(
          'data-test-ents-zoom-image-source',
          `${currentSlide.data.src?.substring(currentSlide.data.src.lastIndexOf('/') + 1)}`,
        );
      }
    });
    this.lightbox.init();
  }

  private handleOpenEvent(event: OpenZoomLayerEvent) {
    if (!this.lightbox || !this.slides || this.slides.length === 0) return;
    togglePageScrolling(false);
    this.lightbox.loadAndOpen(event.detail.index);
  }

  private extractImageDimensionsFromUrl(url: string): { width: string; height: string } {
    const regex = /\/(\d+)x(\d+)\D*\//;
    const match = regex.exec(url);
    if (match) {
      return { width: match[1], height: match[2] };
    }
    return { width: '612', height: '838' };
  }

  private getImageAltText(): string {
    return `${this.brandName} ${this.productName}${this.activeColorName ? ': ' + this.activeColorName : ''}`.trim();
  }

  private attachAiBadgeToSlide(content: { index: number; element?: HTMLElement; slide?: { container?: HTMLElement } }) {
    const container = content.slide?.container;
    if (!container) return;
    container.querySelector('.pswp__ai-badge')?.remove();
    if (!this.slides?.[content.index]?.aiGenerated) return;

    const badge = document.createElement('ents-ai-badge');
    badge.className = 'pswp__ai-badge';
    badge.setAttribute('lang', this.badgeLang);
    badge.setAttribute('country', this.badgeCountry);
    badge.setAttribute('variant', 'overlay-light');
    badge.setAttribute('size', 'small');
    badge.setAttribute('data-test-ents-ai-badge', '');
    badge.addEventListener('click', (event) => event.stopPropagation());
    container.appendChild(badge);

    const image = content.element;
    if (image) {
      const anchorToImageBottom = () => {
        if (image.clientHeight) badge.style.top = `${image.clientHeight}px`;
      };
      anchorToImageBottom();
      const ro = new ResizeObserver(anchorToImageBottom);
      this.resizeObservers.push(ro);
      ro.observe(image);
    }
  }

  protected render() {
    if (!this.slides || this.slides.length === 0) return nothing;
    return html`
      <div class="pswp-gallery" style="display: none;">
        ${this.slides.map((slide) => {
          const { width, height } = this.extractImageDimensionsFromUrl(slide.url);
          return html`
            <a href="${slide.url}" data-pswp-width="${width}" data-pswp-height="${height}" target="_blank">
              <img src="" alt="${this.getImageAltText()}" />
            </a>
          `;
        })}
      </div>
    `;
  }
}
